import React, { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for visual Marquee Selection (Desktop style dragging box)
 * 
 * @param {Array} items - List of student objects {id, ...}
 * @param {Function} setSelectedIds - State setter for selected IDs
 * @param {Object} containerRef - React ref for the scrollable container
 * @param {String} itemSelector - CSS selector to identify item elements (e.g., '.selection-item')
 */
export default function useAttendanceMarqueeSelect(items, setSelectedIds, containerRef, itemSelector = '.selection-item') {
    const [selectionBox, setSelectionBox] = useState(null); // { startX, startY, endX, endY }
    const [isSelecting, setIsSelecting] = useState(false);
    const startPos = useRef({ x: 0, y: 0 });

    const handleMouseDown = useCallback((e) => {
        if (e.button !== 0) return;
        if (e.target.closest('.selection-item')) return;
        if (e.target.closest('button, input, a, .status-controls')) return;

        const container = containerRef.current;
        if (!container) return;

        const rect = container.getBoundingClientRect();
        const x = e.clientX - rect.left + container.scrollLeft;
        const y = e.clientY - rect.top + container.scrollTop;

        startPos.current = { x, y };
        setIsSelecting(true);
        setSelectionBox({ startX: x, startY: y, endX: x, endY: y });

        if (!e.shiftKey) {
            setSelectedIds([]);
        }
    }, [containerRef, setSelectedIds]);

    const handleTouchStart = useCallback((e) => {
        if (e.target.closest('.selection-item')) return;
        if (e.target.closest('button, input, a, .status-controls')) return;

        const container = containerRef.current;
        if (!container) return;

        const touch = e.touches[0];
        const rect = container.getBoundingClientRect();
        const x = touch.clientX - rect.left + container.scrollLeft;
        const y = touch.clientY - rect.top + container.scrollTop;

        startPos.current = { x, y };
        setIsSelecting(true);
        setSelectionBox({ startX: x, startY: y, endX: x, endY: y });
        setSelectedIds([]);
    }, [containerRef, setSelectedIds]);

    const updateSelection = useCallback((clientX, clientY) => {
        const container = containerRef.current;
        if (!container) return;

        const rect = container.getBoundingClientRect();
        const x = clientX - rect.left + container.scrollLeft;
        const y = clientY - rect.top + container.scrollTop;

        setSelectionBox(prev => ({
            ...prev,
            endX: x,
            endY: y
        }));

        const boxLeft = Math.min(startPos.current.x, x);
        const boxRight = Math.max(startPos.current.x, x);
        const boxTop = Math.min(startPos.current.y, y);
        const boxBottom = Math.max(startPos.current.y, y);

        const selected = [];
        const elements = container.querySelectorAll(itemSelector);

        elements.forEach(el => {
            const id = el.getAttribute('data-id') || el.getAttribute('data-attendance-row-id');
            if (!id) return;

            const elLeft = el.offsetLeft;
            const elTop = el.offsetTop;
            const elRight = elLeft + el.offsetWidth;
            const elBottom = elTop + el.offsetHeight;

            const overlaps = !(boxRight < elLeft ||
                boxLeft > elRight ||
                boxBottom < elTop ||
                boxTop > elBottom);

            if (overlaps) {
                selected.push(id);
            }
        });

        setSelectedIds(selected);
    }, [containerRef, itemSelector, setSelectedIds]);

    const handleMouseMove = useCallback((e) => {
        if (!isSelecting) return;
        updateSelection(e.clientX, e.clientY);
    }, [isSelecting, updateSelection]);

    const handleTouchMove = useCallback((e) => {
        if (!isSelecting) return;
        if (e.cancelable) e.preventDefault();
        const touch = e.touches[0];
        updateSelection(touch.clientX, touch.clientY);
    }, [isSelecting, updateSelection]);

    const handleEnd = useCallback(() => {
        setIsSelecting(false);
        setSelectionBox(null);
    }, []);

    useEffect(() => {
        if (isSelecting) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleEnd);
            window.addEventListener('touchmove', handleTouchMove, { passive: false });
            window.addEventListener('touchend', handleEnd);
            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleEnd);
                window.removeEventListener('touchmove', handleTouchMove);
                window.removeEventListener('touchend', handleEnd);
            };
        }
    }, [isSelecting, handleMouseMove, handleTouchMove, handleEnd]);

    // Render the selection box UI
    const renderSelectionBox = () => {
        if (!selectionBox) return null;

        const { startX, startY, endX, endY } = selectionBox;
        const left = Math.min(startX, endX);
        const top = Math.min(startY, endY);
        const width = Math.abs(startX - endX);
        const height = Math.abs(startY - endY);

        return (
            <div
                style={{
                    position: 'absolute',
                    left: `${left}px`,
                    top: `${top}px`,
                    width: `${width}px`,
                    height: `${height}px`,
                    backgroundColor: 'rgba(59, 130, 246, 0.2)', // Blue-500 with opacity
                    border: '1.5px solid rgba(59, 130, 246, 0.5)',
                    borderRadius: '4px',
                    pointerEvents: 'none',
                    zIndex: 100,
                }}
            />
        );
    };

    return {
        handleMouseDown,
        handleTouchStart,
        renderSelectionBox,
        isSelecting
    };
}
